import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, formatDistanceToNow } from "date-fns";
import { Users, Calendar, Clock, FileText, Database, Activity, School, UserCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function SchoolsOverview() {
  const { user } = useAuth();
  const { toast } = useToast();

  if (!user || user.role !== "SUPER_ADMIN") {
    return <Navigate to="/login" replace />;
  }

  // Get current academic year and term
  const { data: currentPeriod, error: periodError } = useQuery({
    queryKey: ['currentPeriod'],
    queryFn: async () => {
      try {
        const { data: activeYear, error: yearError } = await supabase
          .from('academic_years')
          .select('*, terms(*)')
          .eq('is_active', true)
          .single();

        if (yearError) throw yearError;
        
        const activeTerm = activeYear.terms.find((term: any) => term.is_active);
        if (!activeTerm) {
          throw new Error('No active term found');
        }

        return {
          academicYear: activeYear,
          term: activeTerm
        };
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Error fetching academic year",
          description: error.message
        });
        throw error;
      }
    },
  });

  // Get schools data with enhanced statistics
  const { data: schoolsData, error: schoolsError } = useQuery({
    queryKey: ['schoolsOverview', currentPeriod?.academicYear?.id],
    queryFn: async () => {
      if (!currentPeriod) return null;

      try {
        const { data: schools, error } = await supabase
          .from('schools')
          .select(`
            *,
            students:students(count),
            nurses:profiles(
              id,
              name,
              email,
              created_at,
              updated_at
            )
          `)
          .eq('profiles.role', 'NURSE');

        if (error) throw error;

        // Get visits data for each school and nurse
        const schoolsWithStats = await Promise.all(
          schools.map(async (school) => {
            // Get visits per term for the school in current academic year
            const { data: termVisits, error: visitsError } = await supabase
              .from('visits')
              .select('term_id, created_at')
              .eq('school_id', school.id)
              .eq('academic_year_id', currentPeriod.academicYear.id);

            if (visitsError) throw visitsError;

            // Get nurse-specific statistics
            const nursesWithStats = await Promise.all(
              school.nurses.map(async (nurse: any) => {
                try {
                  // Get visits data for this specific nurse
                  const { data: nurseVisits, error: nurseVisitsError } = await supabase
                    .from('visits')
                    .select('created_at, term_id')
                    .eq('nurse_id', nurse.id)
                    .eq('academic_year_id', currentPeriod.academicYear.id)
                    .order('created_at', { ascending: false });

                  if (nurseVisitsError) throw nurseVisitsError;

                  // Get referrals for this nurse
                  const { data: referrals, error: referralsError } = await supabase
                    .from('referrals')
                    .select('created_at')
                    .eq('nurse_id', nurse.id)
                    .eq('academic_year_id', currentPeriod.academicYear.id)
                    .order('created_at', { ascending: false });

                  if (referralsError) throw referralsError;

                  // Calculate visits for current term
                  const visitsThisTerm = nurseVisits?.filter(
                    visit => visit.term_id === currentPeriod.term.id
                  ).length || 0;

                  // Get the most recent activity details
                  const lastVisitDate = nurseVisits?.[0]?.created_at;
                  const lastReferralDate = referrals?.[0]?.created_at;
                  const profileUpdatedDate = nurse.updated_at;

                  // Create array of activities with their dates
                  const activities = [
                    { type: 'Profile updated', date: profileUpdatedDate },
                    { type: 'Visit entry', date: lastVisitDate },
                    { type: 'Referral created', date: lastReferralDate }
                  ].filter(activity => activity.date !== null && activity.date !== undefined);

                  // Sort activities by date and get the most recent
                  const sortedActivities = activities.sort((a, b) => 
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                  );

                  const lastActivity = sortedActivities[0];

                  return {
                    ...nurse,
                    totalVisits: nurseVisits?.length || 0,
                    totalReferrals: referrals?.length || 0,
                    lastVisit: lastVisitDate,
                    lastActivity: lastActivity?.date,
                    lastActivityType: lastActivity?.type || 'No activity',
                    visitsThisTerm
                  };
                } catch (error: any) {
                  console.error('Error fetching nurse statistics:', error);
                  toast({
                    variant: "destructive",
                    title: "Error fetching nurse data",
                    description: error.message
                  });
                  return {
                    ...nurse,
                    totalVisits: 0,
                    totalReferrals: 0,
                    lastVisit: null,
                    lastActivity: nurse.updated_at,
                    lastActivityType: 'Profile updated',
                    visitsThisTerm: 0
                  };
                }
              })
            );

            // Sort terms by name to ensure Term 1, Term 2, Term 3 order
            const sortedTerms = [...currentPeriod.academicYear.terms].sort((a: any, b: any) => {
              const termNumberA = parseInt(a.name.split(' ')[1]);
              const termNumberB = parseInt(b.name.split(' ')[1]);
              return termNumberA - termNumberB;
            });

            // Calculate visits per term for this school
            const visitsPerTerm = sortedTerms.map((term: any) => ({
              termName: term.name,
              visits: termVisits?.filter((v: any) => v.term_id === term.id).length || 0
            }));

            return {
              ...school,
              nurses: nursesWithStats,
              totalStudents: school.students[0].count,
              totalVisits: termVisits?.length || 0,
              visitsPerTerm
            };
          })
        );

        return schoolsWithStats;
      } catch (error: any) {
        console.error('Error processing school statistics:', error);
        toast({
          variant: "destructive",
          title: "Error processing school data",
          description: error.message
        });
        throw error;
      }
    },
    enabled: !!currentPeriod,
  });

  if (periodError || schoolsError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto p-6 pt-[160px]">
          <div className="text-center text-red-500">
            Error loading data. Please try again later.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto p-6 pt-[160px] space-y-6">
        <div className="flex items-center gap-2">
          <Database className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Schools Overview</h1>
        </div>

        {schoolsData?.map((school) => (
          <Card key={school.id} className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <School className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-semibold">{school.name} ({school.prefix})</h2>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">{school.totalStudents} Students</span>
                </div>
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">{school.totalVisits} Visits this Year</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">
                    {currentPeriod?.term.name}, {currentPeriod?.academicYear.name}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              {school.visitsPerTerm?.map((termStat: any) => (
                <Card key={termStat.termName} className="p-4">
                  <h3 className="text-sm font-medium text-muted-foreground">{termStat.termName}</h3>
                  <p className="text-2xl font-bold">{termStat.visits}</p>
                  <p className="text-sm text-muted-foreground">Total Visits</p>
                </Card>
              ))}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nurse Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Last Visit Entry</TableHead>
                  <TableHead>Cases This Term</TableHead>
                  <TableHead>Total Referrals</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {school.nurses.map((nurse: any) => (
                  <TableRow key={nurse.id}>
                    <TableCell className="font-medium">{nurse.name}</TableCell>
                    <TableCell>{nurse.email}</TableCell>
                    <TableCell>
                      {nurse.lastActivity ? (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <div className="flex flex-col">
                            <span>{formatDistanceToNow(new Date(nurse.lastActivity), { addSuffix: true })}</span>
                            <span className="text-xs text-muted-foreground">
                              {nurse.lastActivityType}
                            </span>
                          </div>
                        </div>
                      ) : (
                        "Never"
                      )}
                    </TableCell>
                    <TableCell>
                      {nurse.lastVisit ? (
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          {formatDistanceToNow(new Date(nurse.lastVisit), { addSuffix: true })}
                        </div>
                      ) : (
                        "No visits"
                      )}
                    </TableCell>
                    <TableCell>{nurse.visitsThisTerm} visits</TableCell>
                    <TableCell>{nurse.totalReferrals} referrals</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <UserCheck className={`h-4 w-4 ${nurse.lastActivity ? 'text-green-500' : 'text-yellow-500'}`} />
                        <span className={nurse.lastActivity ? 'text-green-500' : 'text-yellow-500'}>
                          {nurse.lastActivity ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ))}
      </main>
    </div>
  );
}